import type { IRangeValues } from "pro-features/components/filtersPanel/src/RangeFilter";
import { RangeFilter } from "pro-features/components/filtersPanel/src/RangeFilter";
import {
    getCpcSelectedText,
    resetRangeValues,
    cpcValuesFormatter,
    cpcValuesParser,
    isValidCpcRangeValues,
    onDoneCallback,
    onContainerKeyDown,
    INITIAL_RANGE_VALUES,
} from "pro-features/components/filtersPanel/src/RangeFilterUtilityFunctions";
import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import { i18nFilter } from "app/filters/i18nFilter";
import { useWebsiteKeywordsPageTableTopContext } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import React, { useState } from "react";
import { ChipDownContainer } from "@similarweb/ui-components";
import styled from "styled-components";
import { useRankingDistributionTableTopContext } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/useRankingDistributionTableTopContext";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import noop from "lodash/noop";

const RangeFilterContainer = styled.div`
    .range-filter {
        input {
            width: 50px;
        }
    }
`;

const CPC_FILTER_WIDTH = 238;
const CPC_FILTER_BUTTON_TEXT_KEY = "keywords.table.filters.cpc.button";
const CPC_FILTER_DISABLED_TOOLTIP_KEY = "keywords.table.filters.cpc.disabled.tooltip";
const CPC_FROM_PLACEHOLDER = "$0";
const CPC_TO_PLACEHOLDER = ">$20";
const TotalCount = 50 * 1000;

// This is the representational component
export const CpcFilter = ({
    onCpcChange,
    tableFilters,
    tableData,
    disabled,
    tooltipText = null,
    onOpenCpc = noop,
    filterTextKey = CPC_FILTER_BUTTON_TEXT_KEY,
}) => {
    const { cpcFromValue, cpcToValue } = tableFilters;
    const cpcChipDownRef = React.useRef(undefined);
    const [cpcValue, setCpcValue] = useState({ fromValue: cpcFromValue, toValue: cpcToValue });

    const onCpcValueChange = (value) => {
        setCpcValue(value);
    };
    const i18n = i18nFilter();
    if (disabled && (cpcFromValue || cpcToValue)) {
        onCpcChange(INITIAL_RANGE_VALUES);
        proNavigator.applyUpdateParams({
            cpcFromValue: undefined,
            cpcToValue: undefined,
        });
    }
    const onToggle = (isOpen: boolean, isOutsideClick: boolean) => {
        if (isOutsideClick) {
            onDoneCallback(onCpcChange(cpcValue), cpcChipDownRef);
        } else if (isOpen) {
            onOpenCpc();
        }
    };
    const onCloseItem = () => {
        resetRangeValues(onCpcChange)();
        setCpcValue({ fromValue: String(), toValue: String() });
    };
    const onClearCallback = () => {
        setCpcValue({ fromValue: String(), toValue: String() });
    };

    const doneSelected = (rangeValues: IRangeValues) => {
        onDoneCallback(onCpcChange, cpcChipDownRef)(rangeValues);
    };
    return (
        <PlainTooltip
            enabled={disabled}
            text={CPC_FILTER_DISABLED_TOOLTIP_KEY}
        >
            <div onKeyDown={onContainerKeyDown(cpcChipDownRef)}>
                <ChipDownContainer
                    disabled={disabled}
                    closeOnItemClick={false}
                    ref={cpcChipDownRef}
                    width={CPC_FILTER_WIDTH}
                    selectedText={getCpcSelectedText(cpcValue)}
                    buttonText={i18n(filterTextKey)}
                    onCloseItem={onCloseItem}
                    tooltipText={tooltipText}
                    onToggle={onToggle}
                    noResultsMessage={i18n("dropdown.no.results")}
                >
                    {[
                        <RangeFilterContainer key="container">
                            <RangeFilter
                                onDoneCallback={doneSelected}
                                initialFromValue={cpcValue.fromValue}
                                initialToValue={cpcValue.toValue}
                                fromPlaceHolder={CPC_FROM_PLACEHOLDER}
                                toPlaceHolder={CPC_TO_PLACEHOLDER}
                                valuesFormatter={cpcValuesFormatter}
                                valuesParser={cpcValuesParser}
                                isValidRangeValues={isValidCpcRangeValues}
                                onValueChange={onCpcValueChange}
                                onClearCallback={onClearCallback}
                            />
                        </RangeFilterContainer>,
                    ]}
                </ChipDownContainer>
            </div>
        </PlainTooltip>
    );
};

// This are the "connectors" that connect the certain context to the representational component
export const CpcFilterForWebsiteKeywords = () => {
    const { onCpcChange, tableFilters, tableData, onOpenCpc } = useWebsiteKeywordsPageTableTopContext();
    return (
        <CpcFilter
            disabled={tableData?.TotalCount > TotalCount}
            onCpcChange={onCpcChange}
            tableFilters={tableFilters}
            tableData={tableData}
            onOpenCpc={onOpenCpc}
        />
    );
};

export const CpcFilterForRankingDistribution = () => {
    const { onCpcChange, tableFilters, tableData } = useRankingDistributionTableTopContext();
    return (
        <CpcFilter
            disabled={false}
            onCpcChange={onCpcChange}
            tableFilters={tableFilters}
            tableData={tableData}
        />
    );
};
